import { createContext, useContext, useReducer, useEffect } from "react";

// Initial state for the cart
const initialState = {
  items: [],
  subtotal: 0,
  tax: 0,
  shipping: 0,
  total: 0,
  couponCode: null,
  couponDiscount: 0,
};

// Cart reducer function
function cartReducer(state, action) {
  switch (action.type) {
    case "ADD_ITEM": {
      const existingItemIndex = state.items.findIndex(
        (item) =>
          item.id === action.payload.id &&
          JSON.stringify(item.options || {}) ===
            JSON.stringify(action.payload.options || {})
      );

      let updatedItems;
      if (existingItemIndex >= 0) {
        // Item exists, update quantity
        updatedItems = [...state.items];
        updatedItems[existingItemIndex] = {
          ...updatedItems[existingItemIndex],
          quantity:
            updatedItems[existingItemIndex].quantity + action.payload.quantity,
        };
      } else {
        // Add new item
        updatedItems = [...state.items, { ...action.payload }];
      }

      return calculateTotals({ ...state, items: updatedItems });
    }

    case "REMOVE_ITEM": {
      const updatedItems = state.items.filter(
        (item) =>
          !(
            item.id === action.payload.id &&
            JSON.stringify(item.options || {}) ===
              JSON.stringify(action.payload.options || {})
          )
      );
      return calculateTotals({ ...state, items: updatedItems });
    }

    case "UPDATE_QUANTITY": {
      const updatedItems = state.items.map((item) => {
        if (
          item.id === action.payload.id &&
          JSON.stringify(item.options || {}) ===
            JSON.stringify(action.payload.options || {})
        ) {
          return { ...item, quantity: action.payload.quantity };
        }
        return item;
      });
      return calculateTotals({ ...state, items: updatedItems });
    }

    case "CLEAR_CART":
      return calculateTotals({ ...initialState });

    case "APPLY_COUPON":
      return calculateTotals({
        ...state,
        couponCode: action.payload.code,
        couponDiscount: action.payload.discount,
      });

    case "REMOVE_COUPON":
      return calculateTotals({
        ...state,
        couponCode: null,
        couponDiscount: 0,
      });

    case "SET_SHIPPING":
      return calculateTotals({
        ...state,
        shipping: action.payload,
      });

    default:
      return state;
  }
}

// Helper function to calculate cart totals
function calculateTotals(state) {
  const subtotal = state.items.reduce(
    (total, item) => total + item.price * item.quantity,
    0
  );

  // Apply coupon discount if available
  const discountAmount =
    state.couponDiscount > 0 ? subtotal * (state.couponDiscount / 100) : 0;

  const discountedSubtotal = subtotal - discountAmount;

  // Calculate tax (assuming 8% tax rate, adjust as needed)
  const tax = discountedSubtotal * 0.08;

  // Calculate total
  const total = discountedSubtotal + tax + state.shipping;

  return {
    ...state,
    subtotal,
    discountAmount,
    tax,
    total,
  };
}

// Create context
const CartContext = createContext({} as any);

// Cart provider component
export function CartProvider({ children }) {
  const [state, dispatch] = useReducer(cartReducer, initialState);

  // Load cart from localStorage on initial render
  useEffect(() => {
    const savedCart = localStorage.getItem("cart");
    if (savedCart) {
      try {
        const parsedCart = JSON.parse(savedCart);
        Object.keys(parsedCart).forEach((key) => {
          if (key !== "items") return;
          parsedCart.items.forEach((item) => {
            dispatch({
              type: "ADD_ITEM",
              payload: item,
            });
          });
        });
      } catch (error) {
        console.error("Failed to parse saved cart:", error);
      }
    }
  }, []);

  // Save cart to localStorage when it changes
  useEffect(() => {
    localStorage.setItem("cart", JSON.stringify(state));
  }, [state]);

  // Cart actions
  const addItem = (item) => {
    dispatch({ type: "ADD_ITEM", payload: item });
  };

  const removeItem = (item) => {
    dispatch({ type: "REMOVE_ITEM", payload: item });
  };

  const updateQuantity = (item, quantity) => {
    dispatch({
      type: "UPDATE_QUANTITY",
      payload: { ...item, quantity },
    });
  };

  const clearCart = () => {
    dispatch({ type: "CLEAR_CART" });
  };

  const applyCoupon = (code, discount) => {
    dispatch({
      type: "APPLY_COUPON",
      payload: { code, discount },
    });
  };

  const removeCoupon = () => {
    dispatch({ type: "REMOVE_COUPON" });
  };

  const setShipping = (cost) => {
    dispatch({ type: "SET_SHIPPING", payload: cost });
  };

  return (
    <CartContext.Provider
      value={{
        ...state,
        addItem,
        removeItem,
        updateQuantity,
        clearCart,
        applyCoupon,
        removeCoupon,
        setShipping,
      }}
    >
      {children}
    </CartContext.Provider>
  );
}

// Custom hook to use cart context
type CartContextType = {
  items: any[];
  subtotal: number;
  tax: number;
  shipping: number;
  total: number;
  couponCode: string | null;
  couponDiscount: number;
  addItem: (item: any) => void;
  removeItem: (item: any) => void;
  updateQuantity: (item: any, quantity: number) => void;
  clearCart: () => void;
  applyCoupon: (code: string, discount: number) => void;
  removeCoupon: () => void;
  setShipping: (cost: number) => void;
};

export function useCart(): CartContextType {
  const context = useContext(CartContext);
  if (context === undefined) {
    throw new Error("useCart must be used within a CartProvider");
  }
  return context;
}
